import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { gatewayApi } from "@/shared/api";
import { rootReducer } from "@/app/rootReducer";
import { listenerMiddleware } from "@/app/appMiddleware";
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
  useStore,
} from "react-redux";
import { insightsApi, streamsApi } from "@/shared/api";
import { lodApi } from "@/shared/api/lod-api";
import { proximitySlice } from "@/entities/proximity";
import { isViteDevMode } from "@/shared/utils/env";

const persistConfig = {
  key: "root",
  storage,
  whitelist: [proximitySlice.name],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export function makeStore() {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      })
        .prepend(listenerMiddleware.middleware)
        .concat(
          gatewayApi.middleware,
          insightsApi.middleware,
          streamsApi.middleware,
          lodApi.middleware
        ),
    devTools: isViteDevMode(),
  });

  setupListeners(store.dispatch);

  return store;
}

export const appStore = makeStore();
export const persistor = persistStore(appStore);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof appStore.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof appStore.dispatch;
export type AppStore = typeof appStore;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppStore = useStore.withTypes<AppStore>();
